/* __next_internal_client_entry_do_not_use__ MeasurementInput auto */ import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { localNum } from "@faire/web--source/common/localization";
import { NumberParser } from "@faire/web--source/common/NumberParser";
import { Typography } from "@faire/web--source/slate/Typography";
import * as React from "react";
import styled, { css } from "styled-components";
import { Dropdown } from "../Dropdown";
import { Input } from "../Input";
// formatjs makes us choose a number and otherwise defaults to 3,
// I don't think we will ever need more than 10 decimal places,
// so setting here arbitrarily
var MAX_PRECISION = 10;
// JS bails on big numbers. I don't think we have a legitimate reason
// to ever be entering a number larger than this, so setting it as the
// default max to prevent confusion
var MAX_INT = 100000000000000;
/**
 * @trackcomponent
 */ export var MeasurementInput = function(props) {
    var innerRef = React.useRef(null);
    var _props_inputRef;
    var inputRef = (_props_inputRef = props.inputRef) !== null && _props_inputRef !== void 0 ? _props_inputRef : innerRef;
    var dataHjAllow = props["data-hj-allow"];
    var localizeNumber = React.useCallback(function(value) {
        var _localNum;
        return (_localNum = localNum(value, props.precision !== undefined ? {
            minimumFractionDigits: props.precision,
            maximumFractionDigits: props.precision
        } : {
            maximumFractionDigits: MAX_PRECISION
        })) !== null && _localNum !== void 0 ? _localNum : "";
    }, [
        props.precision
    ]);
    var _React_useState = _sliced_to_array(React.useState(props.value !== undefined ? localizeNumber(props.value) : ""), 2), value = _React_useState[0], setValue = _React_useState[1];
    React.useEffect(function() {
        setValue(props.value !== undefined ? localizeNumber(props.value) : "");
    }, [
        props.value,
        localizeNumber
    ]);
    var numberParser = new NumberParser();
    var handleKeyPress = function(e) {
        var _props_onKeyPress;
        (_props_onKeyPress = props.onKeyPress) === null || _props_onKeyPress === void 0 ? void 0 : _props_onKeyPress.call(props, e);
        if (e.key === "Enter") {
            var _inputRef_current;
            inputRef === null || inputRef === void 0 ? void 0 : (_inputRef_current = inputRef.current) === null || _inputRef_current === void 0 ? void 0 : _inputRef_current.blur();
        }
    };
    var handleOnBlur = function(e) {
        var _props_onBlur;
        (_props_onBlur = props.onBlur) === null || _props_onBlur === void 0 ? void 0 : _props_onBlur.call(props, e);
        if (value === "") {
            props.onChange(props.optional ? undefined : props.min);
            return;
        }
        var correctedValue = numberParser.parse(value);
        if (Number.isNaN(correctedValue)) {
            setValue(props.value !== undefined ? localizeNumber(props.value) : "");
            return;
        }
        var _props_min;
        correctedValue = Math.max((_props_min = props.min) !== null && _props_min !== void 0 ? _props_min : -MAX_INT, correctedValue);
        var _props_max;
        correctedValue = Math.min((_props_max = props.max) !== null && _props_max !== void 0 ? _props_max : MAX_INT, correctedValue);
        setValue(localizeNumber(correctedValue));
        props.onChange(numberParser.parse(localizeNumber(correctedValue)));
    };
    var handleOnChange = function(e) {
        setValue(e.target.value);
        // Copy of handleOnBlur, only called if constantUpdate is true
        if (props.applyBlurHandlingOnChange) {
            if (e.target.value === "") {
                props.onChange(props.optional ? undefined : props.min);
                return;
            }
            var correctedValue = numberParser.parse(e.target.value);
            if (Number.isNaN(correctedValue)) {
                setValue(props.value !== undefined ? localizeNumber(props.value) : "");
                return;
            }
            var _props_min;
            correctedValue = Math.max((_props_min = props.min) !== null && _props_min !== void 0 ? _props_min : -MAX_INT, correctedValue);
            var _props_max;
            correctedValue = Math.min((_props_max = props.max) !== null && _props_max !== void 0 ? _props_max : MAX_INT, correctedValue);
            setValue(localizeNumber(correctedValue));
            props.onChange(numberParser.parse(localizeNumber(correctedValue)));
        }
    };
    var renderUnitDropdown = function() {
        var unitInformation = props.unitInformation;
        return unitInformation ? /*#__PURE__*/ _jsx(NarrowDropdown, {
            options: unitInformation.unitOptions,
            onSelect: unitInformation.onUnitChange,
            value: unitInformation.unitValue,
            disabled: props.disabled,
            containingElement: props.containingElement,
            "data-hj-allow": dataHjAllow
        }) : null;
    };
    // 2021/07/28 Terence Dickson
    // The use of `type="text"` here is done in order to support
    // localized number inputs. The HTML `type="number"` type input
    // does not at the time of writing support any sort of localized
    // number input, and its use is recommended against.
    // See FD-28145,
    // https://stackoverflow.com/questions/13412204/localization-of-input-type-number
    var unitDropdown = renderUnitDropdown();
    var _onChange = props.onChange, _suffixNode = props.suffixNode, _value = props.value, _unitInformation = props.unitInformation, _containingElement = props.containingElement, _inputRef = props.inputRef, __dataHjAllow = props["data-hj-allow"], _focus = props.focus, _precision = props.precision, _optional = props.optional, rest = _object_without_properties(props, [
        "onChange",
        "suffixNode",
        "value",
        "unitInformation",
        "containingElement",
        "inputRef",
        "data-hj-allow",
        "focus",
        "precision",
        "optional"
    ]);
    var suffixNodeComponent;
    if (_suffixNode) {
        if (typeof _suffixNode === "string") {
            suffixNodeComponent = /*#__PURE__*/ _jsx(UnitTypography, {
                color: props.disabled ? Core.text.disabled : Core.text.default,
                $hasDropdown: !!unitDropdown,
                children: _suffixNode
            });
        } else {
            suffixNodeComponent = _suffixNode;
        }
    }
    var suffixComponent = suffixNodeComponent || undefined;
    var _props_datatestid;
    return /*#__PURE__*/ _jsx(StyledInput, _object_spread_props(_object_spread({}, rest), {
        inputMode: "decimal",
        type: "text",
        value: value,
        onChange: handleOnChange,
        onBlur: handleOnBlur,
        onKeyPress: handleKeyPress,
        // @ts-expect-error FIXME(implicitAny): https://faire.link/no-implicit-any
        "data-test-id": (_props_datatestid = props["data-test-id"]) !== null && _props_datatestid !== void 0 ? _props_datatestid : "number-input",
        ref: inputRef,
        suffixNode: (_suffixNode !== null && _suffixNode !== void 0 ? _suffixNode : unitDropdown) ? /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                suffixComponent,
                unitDropdown
            ]
        }) : null
    }));
};
var StyledInput = styled(Input).withConfig({
    componentId: "sc-ce5d81b6-0"
})([
    "",
    ";"
], function(props) {
    return props.width !== undefined ? css([
        "width:",
        "px;"
    ], props.width) : "";
});
var UnitTypography = styled(Typography).withConfig({
    componentId: "sc-ce5d81b6-1"
})([
    "position:relative;",
    ";"
], function(props) {
    return props.$hasDropdown ? "right: 8px" : "";
});
var NarrowDropdown = styled(Dropdown).withConfig({
    componentId: "sc-ce5d81b6-2"
})([
    'width:80px;margin-right:-16px;& div[role="button"]{height:40px;border:0;border-left:1px solid ',
    ";&:active,&:focus,&:focus-within{border-left:1px solid ",
    ";}}"
], Core.border.muted, function(props) {
    return props.disabled ? Core.border.muted : Core.border.default;
});
